import React, { useCallback, useEffect, useMemo, useState } from "react";
import NewFilterContainer from "../../../../components/NewFilterContainer ";
import Tooltip from "../../../../components/tooltip";
import MainPageContainer from "../../../../components/MainPageContainer";
import SecondaryHeader from "../../../Rajasthan/Demand/SecondaryHeader";
import { ChartAreaLoader } from "../../../../components/Loading";
import { useSelector } from "react-redux";
import {
  getCollectiveMetrics,
  getExchangeAnalysisData,
} from "../../../../Rest_api/RestApi";
import { ExchangeMetrics } from "../../../../models/metrics";
import { ExchangeAnalysisData, ExchangeAnalysisParams } from "./Overview";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import {
  renderHourTick,
  renderQuarterTick,
} from "../../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
} from "../../../../components/table/SummaryTable";
import { BrushStart, ChartType } from "../../../../models/chart_model";
import { AddDatesWithFileName } from "../../../../helpers/helper";
import FilterMenu from "../../../../components/FilterMenu";
import { ExchangeDataTabs } from "../../../Rajasthan/Demand/Data";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";
import { useLegendFilter } from "../../../../hooks/useLegendFilter";
import MetricChip from "../../../../components/Metrics";
import { ReactComponent as AvgIcon } from "../../../../icons/AvgIcon.svg";
import { ReactComponent as TotalIcon } from "../../../../icons/TotalIcon.svg";
import { normalizeDisplayLabel } from "../../../../utility/displayText";

interface TrendExchangeAnalysisData extends ExchangeAnalysisData {
  trend_label?: string;
}

const chartSuffixes = [
  {
    suffix: "price",
    displayName: "MCP",
    stackSuffix: "price",
    chartType: ChartType.Line,
    unit: "Rs/KWh",
    yAxisId: "right",
  },
  {
    suffix: "mcv",
    displayName: "MCV",
    stackSuffix: "mcv",
    chartType: ChartType.Bar,
    unit: "MW/MWh",
    yAxisId: "left",
  },
  {
    suffix: "purchase_bids",
    displayName: "Purchase Bid",
    stackSuffix: "purchase_bids",
    chartType: ChartType.Line,
    unit: "MW/MWh",
    yAxisId: "left",
  },
  {
    suffix: "sell_bids",
    displayName: "Sell Bid",
    stackSuffix: "sell_bids",
    chartType: ChartType.Line,
    unit: "MW/MWh",
    yAxisId: "left",
  },
];

const Intervals = [
  {
    label: "Time Slot",
    key: "time" as const,
    values: ["x_axis", "second_x_axis"],
    isTime: true,
  },
  {
    label: "Date",
    key: "dateIndex" as const,
    values: ["x_axis"],
    isTime: false,
  },
  {
    label: "Week",
    key: "week" as const,
    values: ["x_axis", "second_x_axis"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month" as const,
    values: ["x_axis"],
    isTime: false,
  },
];

type TrendIntervalKey = (typeof Intervals)[number]["key"];

const exchanges = ["HPX", "IEX", "PXIL"];
const UNIT_OPTIONS = ["MW", "MWh"];
const products = ["DAM", "GDAM", "HPDAM", "RTM"];

const variableFilterOptions = [
  { key: "actual", label: "Actual" },
  { key: "prev_year", label: "Prev Year Actual" },
  { key: "rolling_avg", label: "30D Roll Avg" },
  { key: "prev_year_rolling_avg", label: "Prev Year 30D Roll Avg" },
];

const variableFieldMapping: Record<string, Record<string, string>> = {
  price: {
    prev_year: "prev_year_price",
    rolling_avg: "rolling_actual_30_day_avgs",
    prev_year_rolling_avg: "prev_year_rolling_actual_30_day_avgs",
  },
  mcv: {
    prev_year: "prev_year_mcv",
    rolling_avg: "rolling_actual_30_day_avgs_mcv",
    prev_year_rolling_avg: "prev_year_rolling_actual_30_day_avgs_mcv",
  },
  purchase_bids: {
    prev_year: "prev_year_purchase_bids",
    rolling_avg: "rolling_actual_30_day_avgs_purchase_bids",
    prev_year_rolling_avg: "prev_year_rolling_actual_30_day_avgs_purchase_bids",
  },
  sell_bids: {
    prev_year: "prev_year_sell_bids",
    rolling_avg: "rolling_actual_30_day_avgs_sell_bids",
    prev_year_rolling_avg: "prev_year_rolling_actual_30_day_avgs_sell_bids",
  },
};

const PRICE_TYPE_OPTIONS = [
  { key: "simple_avg" as const, label: "Simple Avg" },
  { key: "wt_avg" as const, label: "Weighted Avg" },
];

const DataTabs = ["comparative", "cumulative"];

const mapTrendIntervalToApiInterval = (
  intervalKey: TrendIntervalKey,
): ExchangeAnalysisParams["interval"] => {
  if (intervalKey === "time") {
    return "slot";
  }
  if (intervalKey === "dateIndex") {
    return "day";
  }
  if (intervalKey === "week") {
    return "week";
  }
  return "month";
};

const FALLBACK_TREND_LABEL = "__default_trend__";

const normalizeAxisDateValue = (
  value: string | null | undefined,
): string | null => {
  if (!value) {
    return null;
  }

  const ddmmyyyy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (ddmmyyyy) {
    return `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`;
  }

  return value;
};

const buildTrendDayLabelLookup = (
  sourceData: TrendExchangeAnalysisData[],
  axisKey: "x_axis" | "second_x_axis",
): Map<string, Map<string, string>> => {
  const trendDateBuckets = new Map<string, Set<string>>();

  sourceData.forEach((item) => {
    const trendKey = item.trend_label || FALLBACK_TREND_LABEL;
    const normalizedDate = normalizeAxisDateValue(item[axisKey]);

    if (!normalizedDate) {
      return;
    }

    if (!trendDateBuckets.has(trendKey)) {
      trendDateBuckets.set(trendKey, new Set<string>());
    }

    trendDateBuckets.get(trendKey)?.add(normalizedDate);
  });

  const trendDayLookup = new Map<string, Map<string, string>>();

  trendDateBuckets.forEach((dateSet, trendKey) => {
    const sortedDates = Array.from(dateSet).sort((a, b) => {
      const aTs = new Date(a).getTime();
      const bTs = new Date(b).getTime();

      if (Number.isNaN(aTs) || Number.isNaN(bTs)) {
        return a.localeCompare(b);
      }

      return aTs - bTs;
    });

    const dayMap = new Map<string, string>();
    sortedDates.forEach((date, index) => {
      dayMap.set(date, `${index + 1} Day`);
    });

    trendDayLookup.set(trendKey, dayMap);
  });

  return trendDayLookup;
};

function createChartObject(
  key: string,
  index: number,
  suffix: string,
  displayName: string,
  stackSuffix: string,
  chartType: ChartType,
  unit: string,
  yAxisId: string,
  selectedUnit: string,
) {
  let actualUnit = unit;
  if (unit === "MW/MWh") {
    actualUnit = selectedUnit;
  }
  const dataKey = `${key}_${suffix}`;
  const legendName = `${normalizeDisplayLabel(key)} ${displayName}`;

  return {
    dataKey,
    name: legendName,
    type: chartType,
    unit: actualUnit,
    yAxisId: yAxisId,
    stackId:
      stackSuffix !== undefined && chartType === ChartType.Bar
        ? stackSuffix
        : undefined,
  };
}

const TrendAnalysis = () => {
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const dataTab = useSelector((state: any) => state.viewMode.activeView);

  const [rawData, setRawData] = useState<TrendExchangeAnalysisData[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);

  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedUnit, setSelectedUnit] = useState(UNIT_OPTIONS[0]);
  const [selectedMetrics, setSelectedMetrics] = useState<any[]>([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);

  const [selectedExchanges, setSelectedExchanges] = useState<string[]>(["IEX"]);
  const [selectedProduct, setSelectedProduct] = useState<string[]>(["DAM"]);
  const [metrics, setMetrics] = useState<ExchangeMetrics | null>(null);
  const [activefunctionTab, setActivefunctionTab] = useState(DataTabs[0]);
  const [selectedPriceType, setSelectedPriceType] = useState(
    PRICE_TYPE_OPTIONS[1],
  );
  const [selectedVariableOptions, setSelectedVariableOptions] = useState<
    string[]
  >(["actual"]);

  const [loading, setLoading] = useState(false);

  const selectedTrendDates = useMemo(
    () => DefaultDates[activeTab]?.defaultTrendDates || [],
    [DefaultDates, activeTab],
  );
  const hasMultipleTrendDates = selectedTrendDates.length > 1;

  const fetchData = useCallback(async () => {
    if (!selectedTrendDates.length) {
      setRawData([]);
      return;
    }

    setLoading(true);
    try {
      const exchangeName =
        selectedExchanges.length === exchanges.length
          ? "all"
          : selectedExchanges.join(",");

      const productName =
        selectedProduct.length === 0 ||
          selectedProduct.length === products.length
          ? "all"
          : selectedProduct.join(",");

      const apiInterval = mapTrendIntervalToApiInterval(selectedInterval.key);
      const selectedBlocks = selectedBlock
        .map((block) => block?.value ?? block)
        .filter((value) => value !== undefined && value !== null);

      const dataByRange = await Promise.all(
        selectedTrendDates.map(async (dateOption: any) => {
          const [startDate, endDate] = String(dateOption?.value || "").split(
            ",",
          );
          if (!startDate || !endDate) {
            return [];
          }

          const rangeData = await getExchangeAnalysisData({
            interval: apiInterval,
            price_type: selectedPriceType.key,
            exchange_name: exchangeName,
            product_name: productName,
            start_date: startDate,
            end_date: endDate,
            function: activefunctionTab,
            blocks: selectedBlocks,
          } as ExchangeAnalysisParams);

          const trendLabel = dateOption?.label || `${startDate} to ${endDate}`;
          return rangeData.map((item) => ({
            ...item,
            trend_label: trendLabel,
          }));
        }),
      );

      setRawData(dataByRange.flat());
    } catch (error) {
      console.error("Error fetching trend analysis data:", error);
      setRawData([]);
    } finally {
      setLoading(false);
    }
  }, [
    selectedTrendDates,
    selectedExchanges,
    selectedProduct,
    selectedInterval.key,
    selectedPriceType.key,
    activefunctionTab,
    selectedBlock,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchMetrics = useCallback(async () => {
    if (!selectedTrendDates.length) {
      return;
    }

    try {
      const exchangeName =
        selectedExchanges.length === exchanges.length
          ? undefined
          : selectedExchanges.length > 0
            ? selectedExchanges
            : undefined;

      const productName =
        selectedProduct.length === products.length
          ? undefined
          : selectedProduct.length > 0
            ? selectedProduct
            : undefined;

      const selectedBlocks = selectedBlock
        .map((block) => block?.value ?? block)
        .filter((value) => value !== undefined && value !== null);

      const ranges = selectedTrendDates
        .map((dateOption: any) => {
          const [startDate, endDate] = String(dateOption?.value || "").split(
            ",",
          );
          if (!startDate || !endDate) {
            return null;
          }

          return {
            startDate,
            endDate,
            exchangeName,
            productName,
            blocks: selectedBlocks,
          };
        })
        .filter((range: any) => range !== null);

      if (!ranges.length) {
        setMetrics(null);
        return;
      }

      const metricsData = await getCollectiveMetrics({
        ranges,
        aggregated: true,
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    }
  }, [selectedTrendDates, selectedExchanges, selectedProduct, selectedBlock]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const processData = useCallback(
    (sourceData: TrendExchangeAnalysisData[]) => {
      let filteredData = [...sourceData];

      if (activefunctionTab === "comparative") {
        if (
          selectedExchanges.length > 0 &&
          selectedExchanges.length < exchanges.length
        ) {
          filteredData = filteredData.filter((item) =>
            selectedExchanges.includes(item.exchange__name),
          );
        }

        if (
          selectedProduct.length > 0 &&
          selectedProduct.length < products.length
        ) {
          filteredData = filteredData.filter((item) =>
            selectedProduct.includes(item.product__name),
          );
        }
      }

      if (selectedBlock.length > 0 && selectedInterval.key === "time") {
        const selectedBlockValues = new Set(
          selectedBlock.map((block) => String(block?.value ?? block)),
        );

        filteredData = filteredData.filter((item) =>
          selectedBlockValues.has(String(item.x_axis)),
        );
      }

      const shouldUseDayIndexLabels =
        selectedInterval.key === "time" || selectedInterval.key === "dateIndex";

      const hasSecondAxis =
        selectedInterval.isTime ||
        selectedInterval.values.includes("second_x_axis");

      const dayLabelLookup = shouldUseDayIndexLabels
        ? buildTrendDayLabelLookup(
          filteredData,
          selectedInterval.key === "time" ? "second_x_axis" : "x_axis",
        )
        : new Map<string, Map<string, string>>();

      const groupedData: Record<string, any> = {};

      filteredData.forEach((item) => {
        const trendKey = item.trend_label || FALLBACK_TREND_LABEL;
        const trendDayMap = dayLabelLookup.get(trendKey);

        let secondAxis = normalizeAxisDateValue(item.second_x_axis);
        let xAxis = normalizeAxisDateValue(item.x_axis) ?? "";

        if (selectedInterval.key === "time" && trendDayMap && secondAxis) {
          secondAxis = trendDayMap.get(secondAxis) || secondAxis;
        }

        if (selectedInterval.key === "dateIndex" && trendDayMap && xAxis) {
          xAxis = trendDayMap.get(xAxis) || xAxis;
        }

        const key =
          hasSecondAxis && secondAxis ? `${secondAxis}_${xAxis}` : xAxis;

        if (!groupedData[key]) {
          groupedData[key] = {
            x_axis: xAxis,
            second_x_axis: secondAxis,
          };
        }

        const exchangeName = item.exchange__name || "";
        const productName = item.product__name || "";
        const basePrefix =
          activefunctionTab === "cumulative"
            ? "Cumulative"
            : exchangeName && productName
              ? `${exchangeName}_${productName}`
              : "Cumulative";

        const prefix =
          hasMultipleTrendDates && item.trend_label
            ? `${item.trend_label}_${basePrefix}`
            : basePrefix;

        selectedMetrics.forEach((typeMetric: any) => {
          let actualSuffix = typeMetric.suffix;
          if (typeMetric.unit === "MW/MWh") {
            actualSuffix = `${typeMetric.suffix}_${selectedUnit.toLowerCase()}`;
          }

          if (selectedVariableOptions.includes("actual")) {
            groupedData[key][`${prefix}_${actualSuffix}`] =
              item[actualSuffix as keyof TrendExchangeAnalysisData];
          }

          selectedVariableOptions.forEach((variableKey) => {
            if (variableKey === "actual") {
              return;
            }

            const variableFieldName =
              variableFieldMapping[typeMetric.suffix]?.[variableKey];
            if (!variableFieldName) {
              return;
            }

            let actualVariableSuffix = variableFieldName;
            if (typeMetric.unit === "MW/MWh") {
              actualVariableSuffix = `${variableFieldName}_${selectedUnit.toLowerCase()}`;
            }

            groupedData[key][`${prefix}_${actualVariableSuffix}`] =
              item[actualVariableSuffix as keyof TrendExchangeAnalysisData];
          });
        });
      });

      const keys =
        activefunctionTab === "cumulative"
          ? Array.from(
            new Set(
              filteredData.map((item) =>
                hasMultipleTrendDates && item.trend_label
                  ? `${item.trend_label}_Cumulative`
                  : "Cumulative",
              ),
            ),
          )
          : Array.from(
            new Set(
              filteredData.map((item) => {
                const exchangeName = item.exchange__name || "";
                const productName = item.product__name || "";
                const baseKey =
                  exchangeName && productName
                    ? `${exchangeName}_${productName}`
                    : "Cumulative";

                if (hasMultipleTrendDates && item.trend_label) {
                  return `${item.trend_label}_${baseKey}`;
                }

                return baseKey;
              }),
            ),
          );

      const groupedRows = Object.values(groupedData);

      return {
        chartData: groupedRows,
        tableData: groupedRows,
        chartKeys: keys,
      };
    },
    [
      activefunctionTab,
      hasMultipleTrendDates,
      selectedExchanges,
      selectedProduct,
      selectedBlock,
      selectedInterval.key,
      selectedInterval.isTime,
      selectedInterval.values,
      selectedMetrics,
      selectedVariableOptions,
      selectedUnit,
    ],
  );

  useEffect(() => {
    if (!rawData.length) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    const processed = processData(rawData);
    setChartData(processed.chartData);
    setTableData(processed.tableData);
    setChartKeys(processed.chartKeys);
  }, [rawData, processData]);

  const memoizedLegends = useMemo(() => {
    const legends: any[] = [];

    chartKeys.forEach((key, index) => {
      selectedMetrics.forEach((typeMetric: any) => {
        let actualSuffix = typeMetric.suffix;
        if (typeMetric.unit === "MW/MWh") {
          actualSuffix = `${typeMetric.suffix}_${selectedUnit.toLowerCase()}`;
        }

        if (selectedVariableOptions.includes("actual")) {
          legends.push(
            createChartObject(
              key,
              index,
              actualSuffix,
              typeMetric.displayName,
              typeMetric.stackSuffix,
              typeMetric.chartType,
              typeMetric.unit,
              typeMetric.yAxisId,
              selectedUnit,
            ),
          );
        }

        selectedVariableOptions.forEach((variableKey) => {
          if (variableKey === "actual") {
            return;
          }

          const variableOption = variableFilterOptions.find(
            (opt) => opt.key === variableKey,
          );
          const variableFieldName =
            variableFieldMapping[typeMetric.suffix]?.[variableKey];
          if (!variableFieldName || !variableOption) {
            return;
          }

          let actualVariableSuffix = variableFieldName;
          if (typeMetric.unit === "MW/MWh") {
            actualVariableSuffix = `${variableFieldName}_${selectedUnit.toLowerCase()}`;
          }

          const variableLabelWithType = `${variableOption.label} (${typeMetric.displayName})`;

          legends.push(
            createChartObject(
              key,
              index,
              actualVariableSuffix,
              variableLabelWithType,
              variableFieldName,
              ChartType.Line,
              typeMetric.unit,
              typeMetric.yAxisId,
              selectedUnit,
            ),
          );
        });
      });
    });

    return legends;
  }, [chartKeys, selectedMetrics, selectedUnit, selectedVariableOptions]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(memoizedLegends);

  const summaryHasSecondAxis =
    selectedInterval.isTime ||
    selectedInterval.values.includes("second_x_axis");

  const summaryExtraKeys = summaryHasSecondAxis
    ? ["x_axis", "second_x_axis"]
    : ["x_axis"];

  const shouldShowDateIndexInTables =
    selectedInterval.key === "time" || selectedInterval.key === "dateIndex";

  const summaryExtraLabels = summaryHasSecondAxis
    ? [
      selectedInterval.isTime ? "Time Slot" : "Week",
      selectedInterval.key === "time"
        ? shouldShowDateIndexInTables
          ? "Date Index"
          : "Date"
        : "Month",
    ]
    : [
      selectedInterval.key === "month"
        ? "Month"
        : shouldShowDateIndexInTables
          ? "Date Index"
          : "Date",
    ];

  const tableUnits: Record<string, string> = {};
  chartSuffixes.forEach((curr) => {
    let actualUnit = curr.unit;
    if (curr.unit === "MW/MWh") {
      actualUnit = selectedUnit;
    }

    tableUnits[curr.suffix] = actualUnit;

    Object.values(variableFieldMapping[curr.suffix] || {}).forEach(
      (fieldName) => {
        tableUnits[fieldName] = actualUnit;
      },
    );
  });

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Exchange Trend Analysis",
      dates: selectedTrendDates,
    }),
    customUnits: tableUnits,
  };

  function secondDataKey() {
    return selectedInterval.isTime ||
      selectedInterval.values.includes("second_x_axis")
      ? "second_x_axis"
      : undefined;
  }

  return (
    <>
      <div className="gas-page-container">
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="Intervals"
              items={Intervals.map((item) => item.label)}
              selectedItems={[selectedInterval.label]}
              onSelect={(_item, index) => {
                setSelectedInterval(Intervals[index]);
                setSelectedBlock([]);
              }}
            />
            <FilterMenu
              title="Unit"
              items={UNIT_OPTIONS}
              selectedItems={[selectedUnit]}
              onSelect={(_item, index) => {
                setSelectedUnit(UNIT_OPTIONS[index]);
              }}
            />
            <FilterMenu
              title="Function"
              items={DataTabs.map(
                (tab) => tab.charAt(0).toUpperCase() + tab.slice(1),
              )}
              selectedItems={[
                activefunctionTab.charAt(0).toUpperCase() +
                  activefunctionTab.slice(1),
              ]}
              onSelect={(_item, index) => {
                setActivefunctionTab(DataTabs[index]);
              }}
            />
            <FilterMenu
              title="Exchanges"
              items={exchanges}
              selectedItems={selectedExchanges}
              setSelectedItems={setSelectedExchanges}
            />
            <FilterMenu
              title="Products"
              items={products}
              selectedItems={selectedProduct}
              setSelectedItems={setSelectedProduct}
            />
            <FilterMenu
              title="Type"
              items={chartSuffixes.map((item) => item.displayName)}
              selectedItems={selectedMetrics.map(
                (item: any) => item.displayName,
              )}
              setSelectedItems={(selectedItems: string[]) => {
                const newSelectedMetrics = chartSuffixes.filter((item) =>
                  selectedItems.includes(item.displayName),
                );
                setSelectedMetrics(newSelectedMetrics);
              }}
            />
            <FilterMenu
              title="Cumulation"
              items={PRICE_TYPE_OPTIONS.map((item) => item.label)}
              selectedItems={[selectedPriceType.label]}
              onSelect={(_item, index) => {
                setSelectedPriceType(PRICE_TYPE_OPTIONS[index]);
              }}
            />
            <FilterMenu
              title="Variable"
              items={variableFilterOptions.map((item) => item.label)}
              selectedItems={variableFilterOptions
                .filter((opt) => selectedVariableOptions.includes(opt.key))
                .map((opt) => opt.label)}
              setSelectedItems={(selectedItems: string[]) => {
                if (!Array.isArray(selectedItems)) {
                  return;
                }

                const newSelectedKeys = variableFilterOptions
                  .filter((opt) => selectedItems.includes(opt.label))
                  .map((opt) => opt.key);

                setSelectedVariableOptions(newSelectedKeys);
              }}
            />
          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
          {metrics &&
            (metrics.weighted_avg_price ||
              metrics.avg_volume ||
              metrics.total_volume_mu) && (
              <div className="flex flex-wrap gap-4 my-0 justify-center">
                {metrics.weighted_avg_price && (
                  <MetricChip
                    icon={<AvgIcon width={26} height={25} />}
                    items={[
                      {
                        label: "Wt. Avg Price",
                        value: metrics.weighted_avg_price.value,
                        unit: metrics.weighted_avg_price.unit,
                      },
                    ]}
                  />
                )}
                {metrics.avg_volume && (
                  <MetricChip
                    icon={<AvgIcon width={26} height={25} />}
                    items={[
                      {
                        label: "Avg Volume",
                        value: metrics.avg_volume.value,
                        unit: metrics.avg_volume.unit,
                      },
                    ]}
                  />
                )}
                {metrics.total_volume_mu && (
                  <MetricChip
                    icon={<TotalIcon width={26} height={25} />}
                    items={[
                      {
                        label: "Total Volume",
                        value: metrics.total_volume_mu.value,
                        unit: metrics.total_volume_mu.unit,
                      },
                    ]}
                  />
                )}
              </div>
            )}
          <div className="gas-chart-container">
            {loading ? (
              <ChartAreaLoader
                title="Loading Trend Analysis"
                subtitle="Fetching exchange trend analysis data..."
              />
            ) : (
              <>
                <div className="gas-heading gap-x-10">
                  <h2 className="chartHeading w-60">Trend Analysis</h2>
                  <div className="flex gap-x-6 justify-end">
                    <BlockRangeSelector
                      blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                      placeholder="Select Slot"
                      selectedOptions={selectedBlock.map((item) => item.value)}
                      setSelectedOptions={setSelectedBlock}
                    />
                  </div>
                </div>

                {dataTab.label === "Chart" && (
                  <ReMixChart
                    onBarClick={(item: any) => {
                      console.log(item);
                    }}
                    data={chartData}
                    showBrush={chartData.length > 96 ? true : undefined}
                    brushStart={BrushStart.Start}
                    brushIndex={
                      chartData.length > 96
                        ? { startIndex: 0, endIndex: 95 }
                        : undefined
                    }
                    isTimeSlot={selectedInterval.isTime}
                    onlyTitle={true}
                    legends={memoizedLegends}
                    onLegendSelect={handleLegendSelect}
                    defaultSelectedLegends={selectedLegends}
                    secondYAxisLabel="Rs/KWh"
                    secondYAxisDomain={[0, 12]}
                    yAxisLabel={selectedUnit}
                    xDataKey="x_axis"
                    xTick={
                      selectedInterval.isTime && selectedBlock.length === 0
                        ? (value: any, _index: any) => {
                            return renderHourTick(value, 10);
                          }
                        : selectedInterval.key === "week"
                          ? renderQuarterTick
                          : undefined
                    }
                    secondXDataKey={secondDataKey()}
                  />
                )}
                {dataTab.label === "Summary" && (
                  <LegendSummaryTable {...tablePar} />
                )}
                {dataTab.label === "Table" && <ChartTable {...tablePar} />}
              </>
            )}
          </div>
          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5 ">
              <Tooltip
                note={
                  <div className="text-start">
                    <p>1. HPX | Hindustan Power Exchange</p>
                    <p>2. IEX | Indian Energy Exchange</p>
                    <p>3. PXIL | Power Exchange of India Limited</p>
                  </div>
                }
                text=" "
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
    </>
  );
};

export default TrendAnalysis;
